import { t } from "@/lib/i18n/interpolate";
import type {
  DocBlock,
  DocSection,
  DocumentModel,
} from "@/lib/tool-kit/document";
import { allocateWords } from "@/lib/tool-kit/essay-templates/research-proposal";
import {
  countWords,
  listOrPrompt,
  paragraphsOrPrompt,
  parseWholeCount,
  splitItems,
  splitLeadIn,
} from "@/lib/tool-kit/essay-templates/text";

/**
 * A discussion section outline in the order Docherty and Smith proposed in the BMJ in
 * 1999 and journals still teach: the principal findings, how the study compares with
 * others, what the findings might mean, implications, limitations, and what is still
 * unanswered — with a closing message. STROBE's discussion items (key results,
 * limitations, interpretation, generalisability) cover the same ground.
 *
 * The tool places the writer's own sentences and shows a sentence frame for anything
 * missing. It cannot judge whether a claim is justified; its only text check is a short
 * list of words that assert proof, which a single study rarely provides.
 */

export type StudyType = "quantitative" | "qualitative" | "other";
export const STUDY_TYPES: readonly StudyType[] = [
  "quantitative",
  "qualitative",
  "other",
];

export interface DiscussionInput {
  title: string;
  aim: string;
  studyType: StudyType;
  /** One principal finding per line. */
  findings: string;
  /** One earlier study per line: "Smith 2019: similar effect, larger sample". */
  comparison: string;
  interpretation: string;
  practice: string;
  theory: string;
  /** One limitation per line. */
  limitations: string;
  /** One unanswered question per line. */
  future: string;
  closing: string;
  words: string;
}

export const DISCUSSION_PARTS = [
  "findings",
  "comparison",
  "interpretation",
  "implications",
  "limitations",
  "future",
  "closing",
] as const;
export type DiscussionPart = (typeof DISCUSSION_PARTS)[number];

/** A planning split, in percent. Comparison with the literature carries the most weight. */
export const DISCUSSION_SHARES: Record<DiscussionPart, number> = {
  findings: 10,
  comparison: 30,
  interpretation: 15,
  implications: 15,
  limitations: 15,
  future: 10,
  closing: 5,
};

export const MIN_DISCUSSION_WORDS = 200;
export const MAX_DISCUSSION_WORDS = 20_000;

export function discussionWordTotal(raw: string): number | null {
  const total = parseWholeCount(raw);
  if (
    total === null ||
    total < MIN_DISCUSSION_WORDS ||
    total > MAX_DISCUSSION_WORDS
  ) {
    return null;
  }
  return total;
}

export function discussionTargets(
  total: number
): Record<DiscussionPart, number> {
  const allocated = allocateWords(
    total,
    DISCUSSION_PARTS.map((part) => DISCUSSION_SHARES[part]),
    10
  );
  return Object.fromEntries(
    DISCUSSION_PARTS.map((part, index) => [part, allocated[index]])
  ) as Record<DiscussionPart, number>;
}

/** Words that assert proof; one study rarely provides it. A prompt to look, not a verdict. */
const OVERCLAIM_PATTERN =
  /\b(?:prove[sd]?|proven|proof|definitively|undoubtedly|conclusively|irrefutabl[ey]|beyond doubt)\b/gi;

function narrative(input: DiscussionInput): string {
  return [
    input.findings,
    input.interpretation,
    input.practice,
    input.theory,
    input.closing,
  ].join("\n");
}

/** Proof words in the sentences the writer drafted, lower-cased and de-duplicated. */
export function overclaims(input: DiscussionInput): string[] {
  const found = (narrative(input).match(OVERCLAIM_PATTERN) ?? []).map((word) =>
    word.toLowerCase()
  );
  return [...new Set(found)];
}

export interface ComparisonRow {
  study: string;
  comparison: string;
}

export function parseComparison(text: string): ComparisonRow[] {
  return splitItems(text).map((line) => {
    const parts = splitLeadIn(line);
    return parts
      ? { study: parts[0], comparison: parts[1] }
      : { study: line, comparison: "" };
  });
}

function has(text: string): boolean {
  return text.trim() !== "";
}

export interface DiscussionCounts {
  words: number;
  findings: number;
  limitations: number;
  comparisons: number;
}

export function discussionCounts(input: DiscussionInput): DiscussionCounts {
  const texts = [
    input.findings,
    input.comparison,
    input.interpretation,
    input.practice,
    input.theory,
    input.limitations,
    input.future,
    input.closing,
  ];
  return {
    words: texts.reduce((sum, text) => sum + countWords(text), 0),
    findings: splitItems(input.findings).length,
    limitations: splitItems(input.limitations).length,
    comparisons: splitItems(input.comparison).length,
  };
}

export type DiscussionCheckId = DiscussionPart | "overclaim";

export interface DiscussionCheck {
  id: DiscussionCheckId;
  state: "done" | "todo" | "note";
  items?: string[];
}

export function discussionChecks(input: DiscussionInput): DiscussionCheck[] {
  const filled: Record<DiscussionPart, boolean> = {
    findings: has(input.findings),
    comparison: has(input.comparison),
    interpretation: has(input.interpretation),
    implications: has(input.practice) || has(input.theory),
    limitations: has(input.limitations),
    future: has(input.future),
    closing: has(input.closing),
  };
  const checks: DiscussionCheck[] = DISCUSSION_PARTS.map((id) => ({
    id,
    state: filled[id] ? "done" : "todo",
  }));
  const claims = overclaims(input);
  checks.push(
    claims.length === 0
      ? { id: "overclaim", state: "done" }
      : { id: "overclaim", state: "note", items: claims }
  );
  return checks;
}

/* ------------------------------------------------------------------ document */

export interface DiscussionLabels {
  defaultTitle: string;
  subtitle: string;
  subtitleWords: string;
  headings: Record<DiscussionPart, string> & { budget: string };
  target: string;
  tips: Record<DiscussionPart, string>;
  tipsLimitations: Record<StudyType, string>;
  aimLabel: string;
  aimPrompt: string;
  prompts: {
    findings: string;
    comparison: string;
    differ: string;
    interpretation: string;
    practice: string;
    theory: string;
    limitations: string;
    future: string;
    closing: string;
  };
  practiceHeading: string;
  theoryHeading: string;
  comparisonHead: { study: string; comparison: string };
  budgetHead: { part: string; words: string };
  budgetTotal: string;
}

type Format = (value: number, options?: Intl.NumberFormatOptions) => string;

function guidance(
  labels: DiscussionLabels,
  tip: string,
  words: number | undefined,
  fmt: Format
): DocBlock {
  return {
    kind: "guidance",
    text:
      words === undefined
        ? tip
        : `${t(labels.target, { words: fmt(words) })} ${tip}`,
  };
}

function comparisonBlocks(
  input: DiscussionInput,
  labels: DiscussionLabels
): DocBlock[] {
  const rows = parseComparison(input.comparison);
  const body: DocBlock[] =
    rows.length === 0
      ? [{ kind: "prompt", text: labels.prompts.comparison }]
      : [
          {
            kind: "table",
            head: [
              labels.comparisonHead.study,
              labels.comparisonHead.comparison,
            ],
            rows: rows.map((row) => [row.study, row.comparison]),
          },
        ];
  return [...body, { kind: "prompt", text: labels.prompts.differ }];
}

function implicationBlocks(
  input: DiscussionInput,
  labels: DiscussionLabels
): DocBlock[] {
  return [
    { kind: "subheading", text: labels.practiceHeading },
    ...paragraphsOrPrompt(input.practice, labels.prompts.practice),
    { kind: "subheading", text: labels.theoryHeading },
    ...paragraphsOrPrompt(input.theory, labels.prompts.theory),
  ];
}

function partBody(
  part: DiscussionPart,
  input: DiscussionInput,
  labels: DiscussionLabels
): DocBlock[] {
  switch (part) {
    case "findings":
      return listOrPrompt(input.findings, "decimal", labels.prompts.findings);
    case "comparison":
      return comparisonBlocks(input, labels);
    case "interpretation":
      return paragraphsOrPrompt(
        input.interpretation,
        labels.prompts.interpretation
      );
    case "implications":
      return implicationBlocks(input, labels);
    case "limitations":
      return listOrPrompt(
        input.limitations,
        "bullet",
        labels.prompts.limitations
      );
    case "future":
      return listOrPrompt(input.future, "decimal", labels.prompts.future);
    default:
      return paragraphsOrPrompt(input.closing, labels.prompts.closing);
  }
}

function budgetSection(
  labels: DiscussionLabels,
  targets: Record<DiscussionPart, number>,
  fmt: Format
): DocSection {
  const total = DISCUSSION_PARTS.reduce((sum, part) => sum + targets[part], 0);
  return {
    heading: labels.headings.budget,
    blocks: [
      {
        kind: "table",
        head: [labels.budgetHead.part, labels.budgetHead.words],
        rows: [
          ...DISCUSSION_PARTS.map((part) => [
            labels.headings[part],
            fmt(targets[part]),
          ]),
          [labels.budgetTotal, fmt(total)],
        ],
      },
    ],
  };
}

export function buildDiscussionOutline(
  input: DiscussionInput,
  labels: DiscussionLabels,
  fmt: Format
): DocumentModel {
  const total = discussionWordTotal(input.words);
  const targets = total ? discussionTargets(total) : null;
  const aim = input.aim.trim();
  const sections: DocSection[] = [
    {
      blocks: [
        aim
          ? { kind: "fields", fields: [{ label: labels.aimLabel, value: aim }] }
          : { kind: "prompt", text: labels.aimPrompt },
      ],
    },
    ...(targets ? [budgetSection(labels, targets, fmt)] : []),
    ...DISCUSSION_PARTS.map((part) => ({
      heading: labels.headings[part],
      blocks: [
        guidance(
          labels,
          part === "limitations"
            ? labels.tipsLimitations[input.studyType]
            : labels.tips[part],
          targets?.[part],
          fmt
        ),
        ...partBody(part, input, labels),
      ],
    })),
  ];
  return {
    title: input.title.trim() || labels.defaultTitle,
    subtitle: total
      ? t(labels.subtitleWords, { words: fmt(total) })
      : labels.subtitle,
    sections,
  };
}
